/**
 * Showcase Slider — frontend bootstrap.
 *
 * One engine, N followers. Embla runs on the media strip only; every other
 * track is moved by reading the engine's own progress:
 *
 *   x = -scrollProgress × (slideCount - 1) × thisTrack'sItemWidth
 *
 * Because each track is measured on its own, the panel's text lags the media
 * by exactly panelInnerWidth / mediaWidth with no ratio written down anywhere
 * — the parallax is a consequence of the layout rather than a tuned constant.
 *
 * A second Embla instance synced on `select` was the obvious alternative and
 * is worse in three ways: reentrant select → scrollTo → select loops, content
 * that stops tracking the finger mid-drag, and a duration mismatch (Embla's
 * `duration` is frame-based, so no CSS transition can be made to match it).
 * Reading progress has none of those.
 *
 * @package BrandyBlocks
 */

const SLIDER_SELECTOR = '.brandy-showcase-slider';

/**
 * Poll for a window global defined by a deferred classic script.
 * Resolves the global or null after timeout (with a console warning).
 *
 * Module scripts can run before deferred classic ones, which is why this
 * exists here as it does in Slider/view.js.
 */
async function waitForGlobal(name, timeoutMs = 2000) {
  const start = Date.now();
  while (!window[name]) {
    if (Date.now() - start > timeoutMs) {
      // eslint-disable-next-line no-console
      console.warn(`[brandy/showcase-slider] ${name} global not found within timeout`);
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return window[name];
}

/**
 * Move every follower track in step with the media strip.
 *
 * @param {Object}      api  Embla instance driving the media strip.
 * @param {HTMLElement} root The slider root.
 * @return {Function} Teardown.
 */
function attachFollowers(api, root) {
  const tracks = Array.from(root.querySelectorAll('[data-showcase-follower]'));
  if (!tracks.length) return () => {};

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let frame = 0;
  let widths = [];

  // Measured from each track's own first item, so a track knows how far one
  // step is for it without being told about any other region.
  const measure = () => {
    widths = tracks.map((track) => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width : 0;
    });
  };

  const steps = () => Math.max(1, api.slideNodes().length - 1);

  const apply = (progress) => {
    tracks.forEach((track, i) => {
      track.style.transform = `translate3d(${-progress * steps() * widths[i]}px, 0px, 0px)`;
    });
  };

  const write = () => {
    frame = 0;
    apply(api.scrollProgress());
  };

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(write);
  };

  // Reduced motion still has to change slides — what it refuses is the drift.
  // Jumping to the resting position for the active index does exactly that.
  const settleToIndex = () => {
    measure();
    apply(api.selectedScrollSnap() / steps());
  };

  const remeasure = () => {
    measure();
    if (reduceMotion) settleToIndex();
    else write();
  };

  measure();
  if (reduceMotion) {
    settleToIndex();
    api.on('select', settleToIndex);
  } else {
    write();
    api.on('scroll', schedule);
    api.on('slideFocus', schedule);
  }
  api.on('reInit', remeasure);
  // Late-loading images change a track's item width after the first measure.
  window.addEventListener('load', remeasure);

  return () => {
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
    api.off('scroll', schedule);
    api.off('slideFocus', schedule);
    api.off('select', settleToIndex);
    api.off('reInit', remeasure);
    window.removeEventListener('load', remeasure);
    tracks.forEach((track) => track.style.removeProperty('transform'));
  };
}

/**
 * Cross-fade the panel to the active slide's colour.
 *
 * The colour belongs to the slide but is painted by the panel, which is the
 * whole point of the panel being a fixed frame: it holds its box and changes
 * colour rather than sliding away with the slide that owns it.
 *
 * @param {Object}      api  Embla instance.
 * @param {HTMLElement} root The slider root.
 * @return {Function} Teardown.
 */
function attachPanelColor(api, root) {
  const panel = root.querySelector('.showcase__panel');
  if (!panel) return () => {};

  let colors = [];
  try {
    colors = JSON.parse(root.dataset.panelColors || '[]');
  } catch (e) {
    return () => {};
  }
  if (!colors.some(Boolean)) return () => {};

  const apply = () => {
    const color = colors[api.selectedScrollSnap()];
    if (color) panel.style.backgroundColor = color;
  };

  apply();
  api.on('select', apply);

  return () => {
    api.off('select', apply);
    panel.style.removeProperty('background-color');
  };
}

async function initSlider(root) {
  const Embla = await waitForGlobal('EmblaCarousel');
  if (!Embla) return;

  const viewportNode = root.querySelector('.showcase__media .embla__viewport');
  if (!viewportNode) return;

  root.classList.add('is-embla-ready');

  // No loop: the followers read scrollProgress(), which stops being linear
  // once Embla wraps around. The block does not offer loop for that reason.
  const api = Embla(viewportNode, { loop: false, align: 'start' });

  const teardowns = [attachFollowers(api, root), attachPanelColor(api, root)];

  const registry = await waitForGlobal('brandyEmbla');
  if (registry) {
    // .is-selected drives the settle on the incoming media half.
    const detachMotion = registry.motion?.attach(api, root);
    if (typeof detachMotion === 'function') teardowns.push(detachMotion);

    if (root.querySelector('.embla__button--prev')) {
      const navFn = registry.nav.resolve(root.getAttribute('data-nav-style') || 'default');
      const cleanup = navFn && navFn(api, root);
      if (typeof cleanup === 'function') teardowns.push(cleanup);
    }
    if (root.querySelector('.embla__dots')) {
      const dotsFn = registry.dots.resolve(root.getAttribute('data-dots-style') || 'fraction');
      const cleanup = dotsFn && dotsFn(api, root);
      if (typeof cleanup === 'function') teardowns.push(cleanup);
    }
  }

  api.on('destroy', () => teardowns.forEach((fn) => fn()));
}

function bootstrap() {
  document.querySelectorAll(SLIDER_SELECTOR).forEach((el) => {
    initSlider(el).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[brandy/showcase-slider] init failed', err);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
